import React from 'react';
import { Spin } from 'antd';
import { formatChatMessageHtml } from '@/common/chat/formatChatMessageHtml';
import { parseChatMessageParts } from '@/common/chat/parseChatMessageParts';
import type { ChatChartDrilldown } from '@/common/chat/parseChatMessageParts';
import ChatMessageChart from './ChatMessageChart';
import AIChatThinkingTimeline from './AIChatThinkingTimeline';
import ChatActionResult, {
  supersededActionResultIndexes,
} from './ChatActionResult';
import { AIChatAskUserSummary } from './AIChatQuestionPanel';
import { findAskUserAnswers } from '../chatActions';
import InsightHelpSource from '@/layout/AppHelpCenter/components/InsightHelpSource/InsightHelpSource';
import {
  AIChatCodeIcon,
  AIChatCloseIcon,
  AIChatContentCopyIcon,
  AIChatThumbDownIcon,
  AIChatThumbUpIcon,
  CheckmarkIcon,
} from '@/assets/icons/icons';
import type {
  AIChatFeedbackRating,
  AIChatMessage,
  AssistantAction,
} from '../types';

type AIChatThreadProps = {
  /** The `ask_user` request currently open as the question panel at the end of
   * the thread -- left out of the thread's read-only summaries so it isn't
   * shown twice. */
  activeAskUserId?: string | null;
  /** Shown after the last message, inside the scrolling thread, so a long
   * question panel scrolls with the conversation instead of taking room from
   * it. */
  trailing?: React.ReactNode;
  activeMessageActions: Record<string, string>;
  copiedActionKeys: Record<string, boolean>;
  hasMessages: boolean;
  messages: AIChatMessage[];
  openQueryMessages: Record<string, boolean>;
  threadRef: React.RefObject<HTMLElement>;
  transcriptLoadingSessionId: string | null;
  copyText: (copyKey: string, text: string) => void;
  onChartDrilldown: (label: string, drilldown: ChatChartDrilldown) => void;
  onFeedback: (
    messageIndex: number,
    rating: AIChatFeedbackRating
  ) => Promise<void>;
  onOpenLink?: (path: string) => void;
  setActiveMessageActions: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setOpenQueryMessages: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
};

const actionIconMap: Record<
  AssistantAction,
  React.FC<{ width?: number; height?: number; color?: string }>
> = {
  thumbUp: AIChatThumbUpIcon,
  thumbDown: AIChatThumbDownIcon,
  copy: AIChatContentCopyIcon,
  code: AIChatCodeIcon,
};

const AIChatThread: React.FC<AIChatThreadProps> = ({
  activeAskUserId,
  trailing,
  activeMessageActions,
  copiedActionKeys,
  hasMessages,
  messages,
  openQueryMessages,
  threadRef,
  transcriptLoadingSessionId,
  copyText,
  onChartDrilldown,
  onFeedback,
  onOpenLink,
  setActiveMessageActions,
  setOpenQueryMessages,
}) => {
  if (transcriptLoadingSessionId !== null) {
    return (
      <section className='ai-chat-drawer__thread' ref={threadRef}>
        <div className='ai-chat-drawer__loading'>Loading conversation...</div>
      </section>
    );
  }

  if (!hasMessages) return null;

  return (
    <section className='ai-chat-drawer__thread' ref={threadRef}>
      {messages.map((message, messageIndex) => {
        if (message.content === 'typing') {
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key -- chat messages and their parts have no ids; the list is append-only and position is the identity (it is also the message_index sent with feedback)
              key={`${messageIndex}-typing`}
              className='ai-chat-drawer__message ai-chat-drawer__message--assistant'
            >
              <div
                className='ai-chat-drawer__streaming-indicator'
                aria-label='Thinking'
              >
                <Spin size='small' />
                <span>Thinking...</span>
              </div>
            </div>
          );
        }

        if (message.type === 'system') {
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key -- chat messages and their parts have no ids; the list is append-only and position is the identity (it is also the message_index sent with feedback)
              key={`${messageIndex}-${message.content}`}
              className='ai-chat-drawer__system-message'
            >
              {message.content}
            </div>
          );
        }

        if (message.type === 'user') {
          // Question-panel answers already show under their question (see
          // AIChatAskUserSummary), so the turn that sent them has no bubble.
          if (message.answers?.action === 'accept') return null;
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key -- chat messages and their parts have no ids; the list is append-only and position is the identity (it is also the message_index sent with feedback)
              key={`${messageIndex}-${message.content}`}
              className='ai-chat-drawer__message ai-chat-drawer__message--user'
            >
              <div className='ai-chat-drawer__user-bubble'>
                {message.content}
              </div>
            </div>
          );
        }

        const parts = message.parts ?? parseChatMessageParts(message.content);
        // A search read several times in one reply shows only its latest result.
        const superseded = supersededActionResultIndexes(parts);

        return (
          <div
            // eslint-disable-next-line react/no-array-index-key -- chat messages and their parts have no ids; the list is append-only and position is the identity (it is also the message_index sent with feedback)
            key={`${messageIndex}-${message.content}`}
            className='ai-chat-drawer__message ai-chat-drawer__message--assistant'
          >
            <div className='ai-chat-drawer__assistant-content'>
              {(message.streaming ||
                (message.thinkingSteps &&
                  message.thinkingSteps.length > 0)) && (
                <AIChatThinkingTimeline
                  steps={message.thinkingSteps ?? []}
                  done={!message.streaming}
                />
              )}
              {/* Help-center answers get the label, blue rule and "Open in Help" links. */}
              <InsightHelpSource helpSource={message.helpSource}>
                {parts.map((part, partIndex) => {
                  if (superseded.has(partIndex)) return null;
                  return part.kind === 'action_result' ? (
                    <ChatActionResult
                      // eslint-disable-next-line react/no-array-index-key -- chat messages and their parts have no ids; the list is append-only and position is the identity (it is also the message_index sent with feedback)
                      key={`${messageIndex}-action-${partIndex}`}
                      result={part.result}
                      onOpenLink={onOpenLink}
                    />
                  ) : part.kind === 'chart' ? (
                    <ChatMessageChart
                      // eslint-disable-next-line react/no-array-index-key -- chat messages and their parts have no ids; the list is append-only and position is the identity (it is also the message_index sent with feedback)
                      key={`${messageIndex}-chart-${partIndex}`}
                      spec={part.spec}
                      onDrilldown={onChartDrilldown}
                    />
                  ) : (
                    <div
                      // eslint-disable-next-line react/no-array-index-key -- chat messages and their parts have no ids; the list is append-only and position is the identity (it is also the message_index sent with feedback)
                      key={`${messageIndex}-text-${partIndex}`}
                      className='ai-chat-drawer__assistant-html'
                      dangerouslySetInnerHTML={{
                        __html: formatChatMessageHtml(part.text),
                      }}
                    />
                  );
                })}
              </InsightHelpSource>
              {message.askUser
                ?.filter((request) => request.id !== activeAskUserId)
                .map((request) => (
                  <AIChatAskUserSummary
                    // eslint-disable-next-line react/no-array-index-key -- chat messages and their parts have no ids; the list is append-only and position is the identity (it is also the message_index sent with feedback)
                    key={`${messageIndex}-ask-${request.id}`}
                    request={request}
                    answers={findAskUserAnswers(
                      messages,
                      messageIndex,
                      request.id
                    )}
                  />
                ))}
              {message.sql && openQueryMessages[String(messageIndex)] && (
                <div className='ai-chat-drawer__sql-card'>
                  <div className='ai-chat-drawer__sql-header'>
                    <span>SQL</span>
                    <div className='ai-chat-drawer__sql-actions'>
                      <button
                        type='button'
                        className='ai-chat-drawer__sql-action'
                        onClick={() =>
                          copyText(
                            `sql-${String(messageIndex)}`,
                            message.sql ?? ''
                          )
                        }
                        aria-label='Copy SQL'
                        data-tooltip='Copy query'
                      >
                        {copiedActionKeys[`sql-${String(messageIndex)}`] ? (
                          <CheckmarkIcon />
                        ) : (
                          <AIChatContentCopyIcon color='currentColor' />
                        )}
                      </button>
                      <button
                        type='button'
                        className='ai-chat-drawer__sql-action'
                        onClick={() =>
                          setOpenQueryMessages((prev) => ({
                            ...prev,
                            [String(messageIndex)]: false,
                          }))
                        }
                        aria-label='Close SQL'
                      >
                        <AIChatCloseIcon color='currentColor' />
                      </button>
                    </div>
                  </div>
                  <pre className='ai-chat-drawer__sql-body'>{message.sql}</pre>
                </div>
              )}
              {message.complete !== false && (
                <div className='ai-chat-drawer__message-actions'>
                  {(
                    [
                      'thumbUp',
                      'thumbDown',
                      'copy',
                      ...(message.sql ? ['code' as const] : []),
                    ] as const
                  ).map((action) => {
                    const Icon = actionIconMap[action];
                    const messageKey = String(messageIndex);
                    const feedbackAction =
                      message.feedback === 'thumbs_up'
                        ? 'thumbUp'
                        : message.feedback === 'thumbs_down'
                          ? 'thumbDown'
                          : undefined;
                    const isCopied =
                      action === 'copy' &&
                      Boolean(copiedActionKeys[`response-${messageKey}`]);
                    const isActive =
                      isCopied ||
                      (action === 'code'
                        ? Boolean(openQueryMessages[messageKey])
                        : activeMessageActions[messageKey] === action ||
                          feedbackAction === action);

                    return (
                      <button
                        key={action}
                        type='button'
                        className={`ai-chat-drawer__message-action ${
                          isActive ? 'is-active' : ''
                        }`}
                        onClick={() => {
                          if (action === 'code') {
                            setOpenQueryMessages((prev) => ({
                              ...prev,
                              [messageKey]: !prev[messageKey],
                            }));
                            return;
                          }
                          if (action === 'copy') {
                            copyText(`response-${messageKey}`, message.content);
                            return;
                          }
                          setActiveMessageActions((prev) => ({
                            ...prev,
                            [messageKey]: action,
                          }));
                          if (action === 'thumbUp') {
                            const _ = onFeedback(messageIndex, 'thumbs_up');
                          }
                          if (action === 'thumbDown') {
                            const _ = onFeedback(messageIndex, 'thumbs_down');
                          }
                        }}
                        aria-label={action === 'code' ? 'Show query' : action}
                        data-tooltip={
                          action === 'copy'
                            ? 'Copy response'
                            : action === 'code'
                              ? 'Show query'
                              : undefined
                        }
                      >
                        {isCopied ? (
                          <CheckmarkIcon />
                        ) : (
                          <Icon color='currentColor' />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {trailing}
    </section>
  );
};

export default AIChatThread;
